import { useState, useEffect, useMemo, useRef } from 'react';
import {
  GALLERY_LIMIT,
  buildGalleryQuery,
  diversifyByCategory,
  galleryWindow,
  romeDay,
  type Event,
} from '@buzzmap/shared';
import { useDiscoveryOptional, buildDiscoveryRequestHeaders } from '../contexts/discoveryStore';
import { takeSsrGallery } from '../utils/ssrBootstrap';
import { useEmbeddedClaim } from './useEmbeddedClaim';

interface UseTopRankedOptions {
  /**
   * ALL resolved search-area centers (T012-FE). Takes precedence over
   * `coords`: a region gallery must query every center, never only the first
   * (plan l.64). Ignored when empty.
   */
  centers?: Array<{ lat: number; lng: number }>;
  /** Legacy single-center input (pre-T012 callers and the legacy window). */
  coords?: [number, number];
  fallbackCity?: string;
  radiusKm?: number; // Default 75
  daysAhead?: number; // Default 30
  limit?: number; // Default 15
  categories?: string[]; // Optional category filter (CSV sent to API). Falls back to no filter if empty.
  /**
   * Explicit date constraint from the URL (T012-FE, plan l.64): intersected
   * with the gallery's own [today, +daysAhead] window. A disjoint range
   * yields an empty gallery, never out-of-filter events.
   */
  startDate?: string;
  endDate?: string;
  /**
   * When true, hold the fetch entirely (no request fires, `loading` stays
   * true). Used on territory routes while `/api/territory` hasn't resolved
   * yet — see `UserContext.locationResolutionPending` — so this gallery
   * doesn't fire a nationwide query that's discarded the instant the real
   * coords land (T014-FE double-fetch fix). Defaults to false.
   */
  skip?: boolean;
}

interface UseTopRankedResult {
  events: Event[];
  loading: boolean;
  error: string | null;
}

/**
 * Top ranked events for the SOPRA gallery (15 cards, 75km, 30 days).
 * Uses popularity_score DESC ordering + round-robin diversification by category.
 * Cache key includes day (Europe/Rome) so Vercel edge cache flips at midnight.
 *
 * T012-FE: accepts ALL resolved centers, intersects explicit URL dates with
 * the gallery window, declares the discovery epoch headers, and — in v2 —
 * never tops a filtered gallery up with out-of-category events (plan l.64).
 */
export function useTopRankedEvents(opts: UseTopRankedOptions): UseTopRankedResult {
  const {
    centers,
    coords,
    fallbackCity,
    radiusKm = 75,
    daysAhead = 30,
    limit = 15,
    categories,
    startDate,
    endDate,
    skip = false,
  } = opts;

  const discovery = useDiscoveryOptional();
  // T018-FE (T018 matrix finding 1): gate on the RESOLVED epoch — the same
  // `mode` signal `EventsContext` (W-D) and `useMapEvents` gate on. The fetch
  // effect below holds while the bootstrap is in flight, so a cold load never
  // fires a headerless pre-bootstrap probe (which the ACTIVATED server
  // answers 426; the old "empty object pre-bootstrap is a pass-through" claim
  // was only true before activation). `null` (no `DiscoveryProvider`: organizer
  // host, isolated tests) fetches as today.
  const mode = discovery?.mode ?? null;
  const headersRef = useRef<Record<string, string>>({});
  // S-1: ref writes belong after commit, not in the render body — and the
  // ref (rather than the headers value) is what keeps a capabilities arrival
  // from re-keying the fetch effect.
  useEffect(() => {
    // T018-FE: the epoch metadata is a v2 declaration. Legacy fetches stay
    // byte-identical to HEAD (no headers); in `v2` the capabilities are always
    // known (the gate cannot resolve to v2 without them), so the very first
    // request of a v2 session is already pinned.
    headersRef.current =
      mode === 'v2' ? buildDiscoveryRequestHeaders(discovery?.capabilities ?? null) : {};
  });

  const embeddedGallery = useEmbeddedClaim<Event[]>();

  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const effectiveCenters = useMemo(() => {
    if (centers && centers.length > 0) return centers;
    if (coords) return [{ lat: coords[0], lng: coords[1] }];
    return undefined;
  }, [centers, coords]);

  const centersKey = effectiveCenters ? JSON.stringify(effectiveCenters) : '';
  const categoriesKey = categories && categories.length > 0 ? [...categories].sort().join(',') : '';
  const fallbackCityKey = fallbackCity ?? '';
  const startDateKey = startDate ?? '';
  const endDateKey = endDate ?? '';

  useEffect(() => {
    // T018-FE: the epoch gate, same shape as EventsContext's W-D branches.
    // - `upgrade-required`: the blocking state owns the session — never fetch.
    // - `loading`: hold until the capabilities bootstrap resolves, so the
    //   session's FIRST gallery request is the negotiated one (v2) or the
    //   HEAD-shaped legacy one — never a headerless pre-bootstrap probe that
    //   426s at an activated epoch. Pre-activation cost: one capabilities RTT,
    //   the same documented delta as EventsContext/useTerritoryRouteResolution.
    if (mode === 'upgrade-required') {
      setLoading(false);
      return;
    }
    if (mode === 'loading') {
      setLoading(true);
      return;
    }

    if (skip) {
      setLoading(true);
      return;
    }

    // Effective window: the gallery's own [today, +daysAhead], narrowed by
    // the explicit URL dates. Disjoint → honest empty, never a broadened
    // fetch outside the user's filter (plan l.60/l.64).
    // The window and the query string come from @buzzmap/shared: the server
    // builds the gallery it embeds in the SSR document with the same code
    // (T204-INT), so "same request" is decided in one place.
    const day = romeDay(new Date());
    const span = galleryWindow({ day, daysAhead, startDate, endDate });
    if (!span) {
      setEvents([]);
      setError(null);
      setLoading(false);
      return;
    }
    const { from, to } = span;

    const parsedCenters: Array<{ lat: number; lng: number }> | undefined = centersKey
      ? (JSON.parse(centersKey) as Array<{ lat: number; lng: number }>)
      : undefined;

    const controller = new AbortController();

    function buildQuery(category?: string): string {
      return buildGalleryQuery({
        day,
        from,
        to,
        centers: parsedCenters,
        radiusKm,
        category,
        fallbackCity,
      });
    }

    // T204-INT: the SSR document may carry this very list. Used only in v2, for
    // the standard 15 cards, and only when the query equals the recorded one
    // character for character (it embeds the Rome day, the centers, the radius
    // and the categories); the events are already diversified. Anything else
    // is the fetch of today.
    if (mode === 'v2' && limit === GALLERY_LIMIT) {
      const primaryQuery = buildQuery(categoriesKey || undefined);
      const embedded = embeddedGallery.claim(primaryQuery, () => takeSsrGallery(primaryQuery));
      if (embedded) {
        setEvents(embedded);
        setError(null);
        setLoading(false);
        return;
      }
    }
    embeddedGallery.release();

    async function fetchEvents(query: string): Promise<Event[]> {
      const res = await fetch(`/api/events?${query}`, {
        signal: controller.signal,
        // Epoch metadata (T010 W6 / T018-FE): pinned in v2 (the activated
        // server 426s unversioned discovery requests); `{}` in legacy, which
        // only exists while the server is NOT activated (pass-through gate).
        // The fetch effect never runs before the mode resolves, so `{}` can
        // no longer mean a pre-bootstrap probe against an activated epoch.
        headers: headersRef.current,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return data.events ?? [];
    }

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const hasCategoryFilter = !!categoriesKey;
        const primary = await fetchEvents(buildQuery(categoriesKey || undefined));
        let diversified = diversifyByCategory(primary, limit);

        // Fill-up: if user preferences narrowed the result below `limit`,
        // top up with popularity-ranked events from other categories.
        // v2 ONLY never fills up outside the active categories (plan l.63/l.64:
        // explicit filters are never silently topped up); legacy keeps HEAD.
        if (hasCategoryFilter && mode !== 'v2' && diversified.length < limit) {
          const fallback = await fetchEvents(buildQuery());
          const seen = new Set(diversified.map((e) => e.id));
          const extras = diversifyByCategory(
            fallback.filter((e) => !seen.has(e.id)),
            limit - diversified.length
          );
          diversified = [...diversified, ...extras];
        }

        setEvents(diversified);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'top-ranked error');
      } finally {
        // T018 review W-2 (same guard as EventsContext): on a mid-flight
        // abort (mode/key change) the rejection lands AFTER the next effect
        // run's `setLoading(true)` — clearing it here would read `loading`
        // false while the replacement fetch is still in flight. On abort
        // there is always a following effect run (which owns the state) or an
        // unmount.
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    load();
    return () => controller.abort();
    // `mode` IS a dependency (T018-FE): the held `loading → legacy/v2`
    // transition is exactly when the cold-start fetch must fire, and
    // `legacy → v2` re-runs it for the no-fill-up contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    centersKey,
    fallbackCityKey,
    radiusKm,
    daysAhead,
    limit,
    categoriesKey,
    startDateKey,
    endDateKey,
    mode,
    skip,
  ]);

  return { events, loading, error };
}
